/**
 * FacultyListPage - Admin list view of all faculty members
 * SDA-123: server-side sort + 50/page + First/Last pagination
 */

import { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  getAdminFaculty,
  getShows,
  type AdminFaculty,
  type FacultyStats,
  type Show,
} from '../../../api/admin';
import SortableHeader from '../../../components/admin/SortableHeader';
import Pagination from '../../../components/admin/Pagination';
import { useDebounce } from '../../../hooks/useDebounce';

const LIMIT = 50;

type FacultyStatus = 'all' | 'invited' | 'onboarding' | 'pending_approval' | 'active' | 'inactive';

// SDA-248: default the list to Active cast members only so the page isn't
// cluttered by inactive/onboarding records. Every other status (incl. "All
// Status" and "Inactive") stays available in the Status dropdown, and a bare
// /admin/faculty URL resets to this default.
const DEFAULT_STATUS: FacultyStatus = 'active';

export default function FacultyListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [faculty, setFaculty] = useState<AdminFaculty[]>([]);
  const [shows, setShows] = useState<Show[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  // Distinguishes the first load (full skeleton) from refetches, where the
  // previous rows stay mounted under a spinner overlay (SDA-264, same pattern
  // as BookingsListPage/InquiriesListPage).
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState<FacultyStatus>(
    (searchParams.get('status') as FacultyStatus) || DEFAULT_STATUS
  );
  const [showFilter, setShowFilter] = useState(searchParams.get('show') || '');
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [page, setPage] = useState(1);

  // SDA-249: debounce the free-text search so we fetch once the user pauses
  // typing, not on every keystroke.
  const debouncedSearch = useDebounce(searchQuery, 300);
  // Guards against out-of-order responses (latest request wins).
  const reqIdRef = useRef(0);

  // Sort
  const [sortBy, setSortBy] = useState('last_name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  // Stats — global whole-database counts from the API (SDA-249)
  const [stats, setStats] = useState<FacultyStats>({
    total: 0,
    active: 0,
    pending_approval: 0,
    onboarding: 0,
    invited: 0,
    inactive: 0,
  });

  useEffect(() => {
    loadShows();
  }, []);

  useEffect(() => {
    loadFaculty();
    // Update URL params
    const params = new URLSearchParams();
    // Only persist a non-default status so a bare /admin/faculty URL stays
    // clean and reloads back to the Active-only default (SDA-248).
    if (statusFilter !== DEFAULT_STATUS) params.set('status', statusFilter);
    if (showFilter) params.set('show', showFilter);
    if (debouncedSearch) params.set('q', debouncedSearch);
    setSearchParams(params);
  }, [statusFilter, showFilter, debouncedSearch, sortBy, sortOrder, page]);

  const loadShows = async () => {
    try {
      const showsData = await getShows();
      setShows(showsData);
    } catch (err) {
      console.error('Failed to load shows:', err);
    }
  };

  const loadFaculty = async () => {
    const reqId = ++reqIdRef.current;
    try {
      setIsLoading(true);
      setError(null);
      const { faculty: facultyData, total: totalCount, stats: statsData } = await getAdminFaculty({
        status: statusFilter !== 'all' ? statusFilter : undefined,
        show_id: showFilter || undefined,
        search: debouncedSearch || undefined,
        page,
        limit: LIMIT,
        sort_by: sortBy,
        sort_order: sortOrder,
      });
      // Ignore stale responses — only the latest request may update state.
      if (reqId !== reqIdRef.current) return;
      setFaculty(facultyData);
      setTotal(totalCount);
      if (statsData) {
        setStats(statsData);
      }
    } catch (err) {
      if (reqId !== reqIdRef.current) return;
      setError(err instanceof Error ? err.message : 'Failed to load faculty');
    } finally {
      if (reqId === reqIdRef.current) {
        setIsLoading(false);
        setHasLoaded(true);
      }
    }
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setPage(1);
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      invited: 'bg-purple-100 text-purple-700',
      onboarding: 'bg-primary-100 text-primary-700',
      pending_approval: 'bg-warning-100 text-warning-700',
      active: 'bg-success-100 text-success-700',
      inactive: 'bg-warm-100 text-warm-600',
    };
    const labels: Record<string, string> = {
      invited: 'Invited',
      onboarding: 'Onboarding',
      pending_approval: 'Pending Approval',
      active: 'Active',
      inactive: 'Inactive',
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || 'bg-warm-100 text-warm-600'}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getStripeStatus = (member: AdminFaculty) => {
    if (!member.stripe_connect_id) {
      return <span className="text-warm-400 text-xs">Not connected</span>;
    }
    if (member.stripe_connect_status === 'enabled') {
      return <span className="text-success-600 text-xs">Enabled</span>;
    }
    if (member.stripe_connect_status === 'restricted') {
      return <span className="text-warning-600 text-xs">Restricted</span>;
    }
    return <span className="text-primary-600 text-xs">Pending</span>;
  };

  // Onboarding progress: 4 steps (W-9 handled by Stripe Connect)
  const getOnboardingProgress = (member: AdminFaculty) => {
    const steps = [
      member.headshot_url,
      member.bio && member.bio.length >= 100,
      member.stripe_connect_status === 'enabled', // Stripe also collects W-9 tax info
      member.shows && member.shows.length > 0,
    ];
    const completed = steps.filter(Boolean).length;
    return { completed, total: 4, percentage: Math.round((completed / 4) * 100) };
  };

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(cents / 100);
  };

  if (isLoading && !hasLoaded) {
    return (
      <div>
        <div className="flex items-center justify-between mb-8">
          <div className="h-10 bg-warm-200 rounded w-1/4 animate-pulse" />
          <div className="h-10 bg-warm-200 rounded w-32 animate-pulse" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white rounded-xl p-3 sm:p-4 animate-pulse">
              <div className="h-8 bg-warm-100 rounded w-1/2 mb-2" />
              <div className="h-4 bg-warm-100 rounded w-3/4" />
            </div>
          ))}
        </div>
        <div className="bg-white rounded-xl shadow-sm">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="p-6 border-b border-warm-100 animate-pulse">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-warm-100 rounded-full" />
                <div className="flex-1">
                  <div className="h-5 bg-warm-100 rounded w-1/3 mb-2" />
                  <div className="h-4 bg-warm-100 rounded w-1/2" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-display font-bold text-warm-900">Faculty</h1>
          <p className="text-warm-600 mt-1 hidden sm:block">
            Manage Broadway cast members and their assignments.
          </p>
        </div>
        <Link to="/admin/faculty/new" className="btn-primary min-h-[44px] flex items-center">
          <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          <span className="hidden sm:inline">Invite </span>Faculty
        </Link>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-sm p-3 sm:p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xl sm:text-2xl font-bold text-warm-900">{stats.total}</p>
              <p className="text-xs sm:text-sm text-warm-600">Total Faculty</p>
            </div>
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-primary-100 flex items-center justify-center">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-3 sm:p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xl sm:text-2xl font-bold text-success-600">{stats.active}</p>
              <p className="text-xs sm:text-sm text-warm-600">Active</p>
            </div>
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-success-100 flex items-center justify-center">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-success-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-3 sm:p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xl sm:text-2xl font-bold text-warning-600">{stats.pending_approval}</p>
              <p className="text-xs sm:text-sm text-warm-600">Pending Approval</p>
            </div>
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-warning-100 flex items-center justify-center">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-warning-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-3 sm:p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xl sm:text-2xl font-bold text-primary-600">{stats.onboarding}</p>
              <p className="text-xs sm:text-sm text-warm-600">Onboarding</p>
            </div>
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-primary-100 flex items-center justify-center">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
        <div className="flex flex-col gap-3">
          {/* Search - full width on mobile */}
          <div className="w-full">
            <label className="block text-sm font-medium text-warm-700 mb-1">
              Search
            </label>
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(1);
                }}
                placeholder="Name, email..."
                className="w-full px-3 py-2.5 pr-9 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              {isLoading && hasLoaded && (
                <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-primary-500 animate-spin" viewBox="0 0 24 24" fill="none" aria-label="Searching">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                </svg>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 sm:flex gap-3">
            {/* Status Filter */}
            <div className="w-full sm:min-w-[150px] sm:w-auto">
              <label className="block text-sm font-medium text-warm-700 mb-1">
                Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value as FacultyStatus);
                  setPage(1);
                }}
                className="w-full px-3 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="invited">Invited</option>
                <option value="onboarding">Onboarding</option>
                <option value="pending_approval">Pending Approval</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            {/* Show Filter */}
            <div className="w-full sm:min-w-[180px] sm:w-auto">
              <label className="block text-sm font-medium text-warm-700 mb-1">
                Show Certification
              </label>
              <select
                value={showFilter}
                onChange={(e) => {
                  setShowFilter(e.target.value);
                  setPage(1);
                }}
                className="w-full px-3 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">All Shows</option>
                {shows.map((show) => (
                  <option key={show.id} value={show.id}>
                    {show.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Clear Filters — restores the Active-only default (SDA-248) */}
            {(statusFilter !== DEFAULT_STATUS || showFilter || searchQuery) && (
              <div className="flex items-end col-span-2 sm:col-span-1">
                <button
                  onClick={() => {
                    setStatusFilter(DEFAULT_STATUS);
                    setShowFilter('');
                    setSearchQuery('');
                    setPage(1);
                  }}
                  className="px-4 py-2.5 min-h-[44px] text-sm text-warm-600 hover:text-warm-900 hover:bg-warm-100 rounded-lg transition-colors"
                >
                  Clear Filters
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-danger-50 border border-danger-200 rounded-lg text-danger-700">
          {error}
        </div>
      )}

      {/* During refetch the previous rows stay mounted, dimmed under a spinner overlay,
          so a search or filter change never blanks the list and repaints (same pattern
          as BookingsListPage). */}
      <div className="relative">
        {isLoading && hasLoaded && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/50 rounded-xl">
            <svg className="w-8 h-8 text-primary-500 animate-spin" viewBox="0 0 24 24" fill="none" aria-label="Loading faculty">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
            </svg>
          </div>
        )}

      {/* Faculty List */}
      {faculty.length === 0 && !isLoading ? (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-warm-100 flex items-center justify-center">
            <svg className="w-8 h-8 text-warm-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-warm-900 mb-2">No faculty found</h3>
          <p className="text-warm-600 mb-6">
            {searchQuery || statusFilter !== 'all' || showFilter
              ? 'Try adjusting your filters to see more results.'
              : 'Start by inviting Broadway cast members to teach.'}
          </p>
          {!searchQuery && statusFilter === 'all' && !showFilter && (
            <Link to="/admin/faculty/new" className="btn-primary">
              Invite Faculty
            </Link>
          )}
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block bg-white rounded-xl shadow-sm overflow-hidden">
            <table className="w-full">
              <thead className="bg-warm-50 border-b border-warm-100">
                <tr>
                  <SortableHeader
                    label="Faculty"
                    field="last_name"
                    currentSort={sortBy}
                    currentOrder={sortOrder}
                    onSort={handleSort}
                  />
                  {/*
                    SDA-123 follow-up: "Shows" is rendered as a plain (non-sortable)
                    header because the worker sorts shows_count post-fetch on the
                    50-row page in memory — not across the full dataset. Exposing
                    a clickable sort arrow was a visual lie for tenants with >50
                    faculty. The worker keeps shows_count in its whitelist as a
                    defensive fallback for any future API client.
                  */}
                  <th className="text-left px-6 py-4 text-sm font-semibold text-warm-700">
                    Shows
                  </th>
                  <SortableHeader
                    label="Status"
                    field="status"
                    currentSort={sortBy}
                    currentOrder={sortOrder}
                    onSort={handleSort}
                  />
                  <th className="text-left px-6 py-4 text-sm font-semibold text-warm-700">
                    Stripe
                  </th>
                  <SortableHeader
                    label="Pay Rate"
                    field="pay_rate_cents"
                    currentSort={sortBy}
                    currentOrder={sortOrder}
                    onSort={handleSort}
                  />
                  {/*
                    SDA-123 follow-up: "Assignments" is rendered as a plain
                    (non-sortable) header for the same reason as "Shows" above —
                    assignments_count is sorted post-fetch on the 50-row page in
                    memory, so a clickable sort header would lie to the user
                    about what's happening across the full dataset.
                  */}
                  <th className="text-left px-6 py-4 text-sm font-semibold text-warm-700">
                    Assignments
                  </th>
                  <th className="text-right px-6 py-4 text-sm font-semibold text-warm-700">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-warm-100">
                {faculty.map((member) => {
                  const progress = getOnboardingProgress(member);
                  const isOnboarding = member.status === 'onboarding' || member.status === 'pending_approval';

                  return (
                    <tr key={member.id} className="hover:bg-warm-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          {member.headshot_url ? (
                            <img
                              src={member.headshot_url}
                              alt={member.name}
                              className="w-10 h-10 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-warm-200 flex items-center justify-center">
                              <span className="text-warm-600 font-medium">
                                {member.name?.charAt(0) || '?'}
                              </span>
                            </div>
                          )}
                          <div>
                            <p className="font-medium text-warm-900">{member.name}</p>
                            <p className="text-sm text-warm-500">{member.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {member.shows && member.shows.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {member.shows.slice(0, 2).map((show) => (
                              <span
                                key={show.id}
                                className="px-2 py-0.5 bg-primary-50 text-primary-700 rounded text-xs"
                              >
                                {show.name}
                              </span>
                            ))}
                            {member.shows.length > 2 && (
                              <span className="px-2 py-0.5 bg-warm-100 text-warm-600 rounded text-xs">
                                +{member.shows.length - 2}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-warm-400 text-sm">None</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-1">
                          {getStatusBadge(member.status)}
                          {isOnboarding && (
                            <div className="flex items-center gap-2">
                              <div className="w-16 h-1.5 bg-warm-200 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-primary-500 rounded-full"
                                  style={{ width: `${progress.percentage}%` }}
                                />
                              </div>
                              <span className="text-xs text-warm-500">
                                {progress.completed}/{progress.total}
                              </span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {getStripeStatus(member)}
                      </td>
                      <td className="px-6 py-4">
                        <span className="font-medium text-warm-900">
                          {member.pay_rate_cents
                            ? formatCurrency(member.pay_rate_cents)
                            : '--'}
                        </span>
                        <span className="text-warm-500 text-sm">/class</span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-center">
                          <p className="font-semibold text-warm-900">
                            {member.upcoming_assignments || 0}
                          </p>
                          <p className="text-xs text-warm-500">upcoming</p>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <Link
                          to={`/admin/faculty/${member.id}`}
                          className="px-3 py-1.5 text-sm text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors"
                        >
                          View Profile
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {faculty.map((member) => {
              const progress = getOnboardingProgress(member);
              const isOnboarding = member.status === 'onboarding' || member.status === 'pending_approval';

              return (
                <Link
                  to={`/admin/faculty/${member.id}`}
                  key={member.id}
                  className="block bg-white rounded-xl border border-warm-200 p-4 active:scale-[0.98] transition-transform hover:border-warm-300"
                >
                  <div className="flex items-center gap-3 mb-2">
                    {member.headshot_url ? (
                      <img
                        src={member.headshot_url}
                        alt={member.name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-warm-200 flex items-center justify-center">
                        <span className="text-warm-600 font-medium">
                          {member.name?.charAt(0) || '?'}
                        </span>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-warm-900 truncate">{member.name}</p>
                      <p className="text-sm text-warm-500 truncate">{member.email}</p>
                    </div>
                  </div>
                  {member.shows && member.shows.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-2">
                      {member.shows.slice(0, 3).map((show) => (
                        <span
                          key={show.id}
                          className="px-2 py-0.5 bg-primary-50 text-primary-700 rounded text-xs"
                        >
                          {show.name}
                        </span>
                      ))}
                      {member.shows.length > 3 && (
                        <span className="px-2 py-0.5 bg-warm-100 text-warm-600 rounded text-xs">
                          +{member.shows.length - 3}
                        </span>
                      )}
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {getStatusBadge(member.status)}
                      {isOnboarding && (
                        <div className="flex items-center gap-1">
                          <div className="w-12 h-1.5 bg-warm-200 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-primary-500 rounded-full"
                              style={{ width: `${progress.percentage}%` }}
                            />
                          </div>
                          <span className="text-xs text-warm-500">
                            {progress.completed}/{progress.total}
                          </span>
                        </div>
                      )}
                    </div>
                    {(member.upcoming_assignments || 0) > 0 && (
                      <span className="text-xs text-warm-600">
                        {member.upcoming_assignments} upcoming
                      </span>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>

          {/* Pagination */}
          <Pagination
            page={page}
            total={total}
            limit={LIMIT}
            itemLabel="faculty"
            onPageChange={setPage}
          />
        </>
      )}
      </div>
    </div>
  );
}
